import { z } from 'zod'

/**
 * Encrypted string format: "encrypted:{version}:{ciphertext}"
 * Example: "encrypted:v1:abc123..."
 *
 * The version (v1, v2, etc.) maps to a Netlify environment variable
 * containing the encryption key for that version.
 */
export const ENCRYPTED_STRING_PATTERN = /^encrypted:(v\d+):(.+)$/

/**
 * Custom Zod type for HopDrive encrypted strings.
 * Use this in config schemas where values should be encrypted at rest.
 *
 * The branded type allows TypeScript to distinguish encrypted strings
 * from regular strings, preventing accidental use of unencrypted values.
 */
export const encryptedString = z
  .string()
  .refine((val) => ENCRYPTED_STRING_PATTERN.test(val), {
    message: 'Must be a valid encrypted string format: encrypted:{version}:{ciphertext}',
  })
  .brand<'EncryptedString'>()

export type EncryptedString = z.infer<typeof encryptedString>

/**
 * Helper to check if a string is in encrypted format.
 * Useful for runtime checks without full Zod validation.
 */
export function isEncryptedString(value: unknown): value is string {
  return typeof value === 'string' && ENCRYPTED_STRING_PATTERN.test(value)
}

/**
 * Extract the version from an encrypted string.
 * Returns null if not a valid encrypted string.
 */
export function getEncryptionVersion(value: string): string | null {
  const match = value.match(ENCRYPTED_STRING_PATTERN)
  return match ? match[1] : null
}

/**
 * Extract the ciphertext from an encrypted string.
 * Returns null if not a valid encrypted string.
 */
export function getCiphertext(value: string): string | null {
  const match = value.match(ENCRYPTED_STRING_PATTERN)
  return match ? match[2] : null
}
